import { isAuthServiceUnavailableMessage } from "@/features/auth/constants"

export type LoginErrorKind =
  | "email_not_confirmed"
  | "rate_limited"
  | "captcha_failed"
  | "network"
  | "invalid_credentials"

interface LoginErrorLike {
  code?: string
  status?: number
  message?: string
}

// Gateway and Cloudflare origin errors, matching supabase-js's retryable statuses.
const RETRYABLE_SERVER_STATUSES = new Set([502, 503, 504, 520, 521, 522, 523, 524, 530])

/**
 * Classify a Supabase `signInWithPassword` error so the login UI can show the
 * right message instead of reporting every failure as bad credentials.
 */
export function getLoginErrorKind(error: LoginErrorLike): LoginErrorKind {
  const msg = error.message?.toLowerCase() ?? ""

  if (
    error.code === "email_not_confirmed" ||
    msg.includes("email not confirmed") ||
    msg.includes("not confirmed") ||
    msg.includes("email_not_confirmed") ||
    msg.includes("confirm your email")
  ) {
    return "email_not_confirmed"
  }
  if (
    error.status === 429 ||
    error.code === "over_request_rate_limit" ||
    msg.includes("rate limit")
  ) {
    return "rate_limited"
  }
  if (error.code === "captcha_failed" || msg.includes("captcha")) {
    return "captcha_failed"
  }
  if (
    error.status === 0 ||
    (error.status !== undefined && RETRYABLE_SERVER_STATUSES.has(error.status)) ||
    isAuthServiceUnavailableMessage(error.message)
  ) {
    return "network"
  }
  return "invalid_credentials"
}

// Messages browsers put on the TypeError a failed fetch throws (Chrome, Firefox, Safari).
const FETCH_FAILURE_MESSAGES = ["failed to fetch", "networkerror when attempting to fetch resource", "load failed"]

/**
 * Classify a value thrown during login. Returns `null` for errors that are not
 * network failures so callers keep their generic handling.
 */
export function getThrownLoginErrorKind(thrown: unknown): "network" | null {
  if (typeof navigator !== "undefined" && navigator.onLine === false) return "network"
  if (!(thrown instanceof Error)) return null
  if (thrown.name === "AuthRetryableFetchError") return "network"
  const msg = thrown.message.toLowerCase()
  if (thrown.name === "TypeError" && FETCH_FAILURE_MESSAGES.some((m) => msg.includes(m))) {
    return "network"
  }
  return null
}

export type PasswordLoginOutcome =
  | { type: "success" }
  | { type: "precheck_failed"; message: string }
  | { type: "failed"; kind: LoginErrorKind }
  | { type: "unknown_error" }

/**
 * Run the email login (server pre-check, then Supabase password sign-in) and
 * classify every failure, including thrown ones such as an offline fetch.
 */
export async function runPasswordLogin(steps: {
  preCheck: () => Promise<{ error: string | null }>
  signInWithPassword: () => Promise<{ error: LoginErrorLike | null }>
}): Promise<PasswordLoginOutcome> {
  try {
    const result = await steps.preCheck()
    if (result.error) return { type: "precheck_failed", message: result.error }
    const { error } = await steps.signInWithPassword()
    if (error) return { type: "failed", kind: getLoginErrorKind(error) }
    return { type: "success" }
  } catch (thrown) {
    const kind = getThrownLoginErrorKind(thrown)
    return kind ? { type: "failed", kind } : { type: "unknown_error" }
  }
}

/** `auth.login.*` translation key shown for each non-unverified failure. */
export const LOGIN_ERROR_MESSAGE_KEY = {
  rate_limited: "rateLimited",
  captcha_failed: "captchaFailed",
  network: "networkError",
  invalid_credentials: "invalidCredentials",
} as const satisfies Record<Exclude<LoginErrorKind, "email_not_confirmed">, string>
